import Box from "@mui/material/Box";
import Skeleton from "@mui/material/Skeleton";
import { useCallback, useMemo, useRef, useState } from "react";
import { MultiValueStackedBarChart } from "../../../../../components/charts/MultiValueStackedBarChart/MultiValueStackedBarChart";
import { MultiValueStackedBarChartData } from "../../../../../components/charts/MultiValueStackedBarChart/types";
import { useGetMarketSizeBySegment } from "./hooks/useGetMarketSizeBySegment";
import { useWorkspaceStore } from "../../../../../store/workspace/WorkspaceStore";
import { ChartExportMenu } from "../../../../../components/charts/ChartExportMenu";
import * as am4charts from "@amcharts/amcharts4/charts";
import { TAB_VIEW } from "../../../WorkspaceConfiguration/constants";
import { useParams, useNavigate } from "react-router-dom";
import { useSnackbar } from "../../../../../components/Snackbar";
import { getInsufficientInfoText } from "../utils";
import { TitleWithTooltipInfo } from "../../../../../components/charts/TitleWithTooltipInfo";
import {
  CHART_CONSTANTS,
  INSUFFICIENT_WHITESPACE_INFO_TEXT,
} from "../../constants";

const { chartTitle, baseId, tooltip, csvFieldMappings } =
  CHART_CONSTANTS.marketSizeBySegment;

export const MarketSizeBySegment = () => {
  const [isChartHovered, setIsChartHovered] = useState(false);
  const { tam, sam, som, isLoading } = useGetMarketSizeBySegment();

  const isSamConfigured = !useWorkspaceStore((state) =>
    state.getIsSamDisabled()
  );
  const isSomConfigured = !useWorkspaceStore((state) =>
    state.getIsSomDisabled()
  );
  const whitespace = useWorkspaceStore(
    (state) => state.draft?.configuration?.whitespace
  );

  const isWhitespaceConfigured = !!whitespace;
  const isHierarchyMatchingEnabled = whitespace?.enableHierarchyClassification;

  const chartRef = useRef<am4charts.XYChart>(null);

  const navigate = useNavigate();
  const { id: workspaceId } = useParams();
  const { showInfo } = useSnackbar();

  const chartData = useMemo<MultiValueStackedBarChartData[]>(() => {
    return [
      {
        segmentName: "TAM",
        current: tam.currentCustomers,
        whitespace: tam.adjustedWhitespace,
        expansion: tam.adjustedExpansion,
        prospect: tam.adjustedProspect,
        totalSpend: tam.totalSpend,
        totalCompanies: tam.companyCount,
        // TAM 'should' always be configured
        isSegmentConfigured: true,
        isAdditionalInfoConfigured: isWhitespaceConfigured,
      },
      {
        segmentName: "SAM",
        current: isSamConfigured ? sam.currentCustomers : 0,
        whitespace: isSamConfigured ? sam.adjustedWhitespace : 0,
        expansion: isSamConfigured ? sam.adjustedExpansion : 0,
        prospect: isSamConfigured ? sam.adjustedProspect : 0,
        totalSpend: isSamConfigured ? sam.totalSpend : 0,
        totalCompanies: isSamConfigured ? sam.companyCount : 0,
        isSegmentConfigured: isSamConfigured,
        isAdditionalInfoConfigured: isWhitespaceConfigured,
      },
      {
        segmentName: "SOM",
        current: isSomConfigured ? som.currentCustomers : 0,
        whitespace: isSomConfigured ? som.adjustedWhitespace : 0,
        expansion: isSomConfigured ? som.adjustedExpansion : 0,
        prospect: isSomConfigured ? som.adjustedProspect : 0,
        totalSpend: isSomConfigured ? som.totalSpend : 0,
        totalCompanies: isSomConfigured ? som.companyCount : 0,
        isSegmentConfigured: isSomConfigured,
        isAdditionalInfoConfigured: isWhitespaceConfigured,
      },
    ];
  }, [
    sam.companyCount,
    sam.currentCustomers,
    sam.totalSpend,
    sam.adjustedWhitespace,
    sam.adjustedExpansion,
    sam.adjustedProspect,
    som.companyCount,
    som.currentCustomers,
    som.totalSpend,
    som.adjustedWhitespace,
    som.adjustedExpansion,
    som.adjustedProspect,
    tam.companyCount,
    tam.currentCustomers,
    tam.totalSpend,
    tam.adjustedWhitespace,
    tam.adjustedExpansion,
    tam.adjustedProspect,
    isSamConfigured,
    isSomConfigured,
    isWhitespaceConfigured,
  ]);
  const shouldShowExport = chartRef && !isLoading;

  const handleInsufficientButtonClick = useCallback(
    (segment: TAB_VIEW) => {
      navigate(`/projects/${workspaceId}/configuration`);
      showInfo(getInsufficientInfoText(segment));
    },
    [navigate, showInfo, workspaceId]
  );

  const handleWhitespaceConfigClick = useCallback(() => {
    navigate(`/projects/${workspaceId}/configuration/whitespace`);
    showInfo(INSUFFICIENT_WHITESPACE_INFO_TEXT);
  }, [navigate, showInfo, workspaceId]);

  return (
    <Box
      id={`${baseId}-container`}
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "16px",
        bgcolor: "#fff",
        height: "350px",
        borderRadius: "10px",
        border: "solid 1px #edeff1",
        padding: "20px 19px 15px 20px",
      }}
      onMouseEnter={() => setIsChartHovered(true)}
      onMouseLeave={() => setIsChartHovered(false)}
    >
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        width="100%"
      >
        <TitleWithTooltipInfo
          chartTitle={chartTitle}
          tooltipTitle={tooltip}
          showInfoIcon={isChartHovered}
        />

        {shouldShowExport && (
          <ChartExportMenu
            chart={chartRef}
            title={chartTitle}
            csvFieldMappings={csvFieldMappings}
          />
        )}
      </Box>
      {isLoading ? (
        <Skeleton variant="rounded" width="100%" height="300px" />
      ) : (
        <MultiValueStackedBarChart
          id={baseId}
          chartData={chartData}
          onInsufficientButtonClick={handleInsufficientButtonClick}
          onAdditionalInfoLinkClick={handleWhitespaceConfigClick}
          ref={chartRef}
          isHierarchyMatchingEnabled={Boolean(
            isWhitespaceConfigured && isHierarchyMatchingEnabled
          )}
        />
      )}
    </Box>
  );
};
